import axiosClient from "./apiClient";
import {
  getUserConfig,
  putUserConfig,
  type AmbientAlertsDto,
} from "./userConfig";

/**
 * UI model — temperatures stored in Celsius (the firmware's native unit). The
 * display layer converts to whichever unit the user picked via the F/C toggle.
 *
 * Maps to the API's existing `UserConfigDto.AmbientAlerts` shape
 * (`{enabled, lowC, highC}`). We rename to `minC/maxC` for the UI because
 * "minimum / maximum" reads better than "low / high" in form labels.
 */
export type HomeTemperatureThreshold = {
  enabled: boolean;
  minC: number;
  maxC: number;
};

/**
 * Latest ambient reading the device reported, used for the "Home Temp" column
 * and the Sensor Detail stat. `measuredAt` is an ISO string in UTC.
 *
 * NOTE, because this is a SECOND timestamp convention on screens that already
 * have one: unitEventsUtils parses the alarm records' space-separated `datetime`
 * with `.replace(" ", "T")` and no zone suffix, which JavaScript reads as LOCAL
 * time, in 8 places. So a UTC value here sits beside local-naive values there,
 * and comparing a "last reading" time against an alarm time can be hours out.
 * Whether that space-separated form is really UTC is not established -- see
 * cts_app_web#62, which also explains why unifying them is its own change rather
 * than a fold-in here. Do not compare these two across the boundary until it is.
 * Returns
 * null while the API endpoint for this isn't deployed yet — the column will
 * just render "—" until it lands.
 */
export type AmbientTemperatureReading = {
  temperatureC: number;
  measuredAt: string;
};

// Sensible fallback when the device has never reported a config back yet —
// matches the firmware's default comfort zone constants.
export const DEFAULT_HOME_TEMPERATURE_THRESHOLD: HomeTemperatureThreshold = {
  enabled: false,
  minC: 18,
  maxC: 27,
};

// AmbientAlertsDto + the full UserConfigDto now live in userConfig.ts.
// The dashboard's single source of truth for the shared cloud payload.

function dtoToThreshold(
  aa: AmbientAlertsDto | null | undefined
): HomeTemperatureThreshold | null {
  if (!aa) return null;
  return {
    enabled: !!aa.enabled,
    minC: Number(aa.lowC),
    maxC: Number(aa.highC),
  };
}

function thresholdToDto(t: HomeTemperatureThreshold): AmbientAlertsDto {
  return {
    enabled: !!t.enabled,
    lowC: t.minC,
    highC: t.maxC,
  };
}

/**
 * Fetch the home-temperature threshold the device currently holds. Reads from
 * the API's `user-config` endpoint, which returns the whole UserConfigDto
 * bundle (we just pull `ambientAlerts` out). Returns null when:
 *   - the sensor row exists but has no userConfig yet (fresh sensor), or
 *   - the call 404s.
 * Callers should treat null as "no config yet" and fall back to
 * DEFAULT_HOME_TEMPERATURE_THRESHOLD in the editor.
 */
export async function getHomeTemperatureThreshold(
  name: string
): Promise<HomeTemperatureThreshold | null> {
  const dto = await getUserConfig(name);
  return dtoToThreshold(dto?.ambientAlerts);
}

/**
 * Push a threshold to the device. Uses the existing partial-update semantics
 * on `PUT /user-config`: we only send `ambientAlerts`, so the other
 * UserConfigDto fields (unattendedSpeed, auxLights, speakerVolume) are
 * preserved both server-side and on the device.
 *
 * Resolves on the server's 200 — the API both writes the row AND publishes to
 * `cmd/cts-scs/{name}/user-config-v1`. A non-200 (e.g. 502 if the publish to
 * the device failed) bubbles up so the card can show "couldn't reach the
 * device". We deliberately don't poll for the device's ack here to match the
 * mobile app's fire-and-display pattern; the next refetch will reflect the
 * applied value once the device acks.
 */
export async function setHomeTemperatureThreshold(
  name: string,
  threshold: HomeTemperatureThreshold
): Promise<void> {
  await putUserConfig(name, { ambientAlerts: thresholdToDto(threshold) });
}

/**
 * Latest ambient temperature reading from the telemetry table. We read page 1
 * of the per-sensor ambient-temperature feed (sorted newest first) and return
 * the first entry. Returns null when:
 *   - the endpoint 404s (sensor unknown OR the API hasn't deployed this
 *     route yet)
 *   - the page is empty (no reading has been recorded for this sensor yet —
 *     fresh install, or the firmware/IoT rule that ingests ambient telemetry
 *     hasn't landed).
 * Either way the Home Temp column just renders "—" until a reading arrives.
 */
type AmbientTelemetryDto = {
  name: string;
  id: string;
  datetime: string;
  dateCreated: string;
  temperatureC: number;
};

type PagedAmbientResponse = {
  data?: AmbientTelemetryDto[];
  nextPageToken?: string;
  size?: number;
};

export async function getAmbientTemperature(
  name: string
): Promise<AmbientTemperatureReading | null> {
  try {
    const res = await axiosClient.get<PagedAmbientResponse>(
      `telemetry/${name}/ambient-temperature`,
      { params: { PageSize: 1 } }
    );
    const first = res.data?.data?.[0];
    if (!first) return null;
    const temp = Number(first.temperatureC);
    if (!Number.isFinite(temp) || !first.datetime) return null;
    return { temperatureC: temp, measuredAt: first.datetime };
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

/**
 * Tone for displaying an ambient reading against a configured threshold:
 *   - "hot"     → above max  → red
 *   - "cold"    → below min  → blue
 *   - "ok"      → inside range
 *   - "nominal" → no threshold configured (or disabled)
 *   - "unknown" → no reading
 */
export type AmbientTone = "hot" | "cold" | "ok" | "nominal" | "unknown";

export function ambientTone(
  reading: AmbientTemperatureReading | null,
  threshold: HomeTemperatureThreshold | null
): AmbientTone {
  if (!reading) return "unknown";
  if (!threshold || !threshold.enabled) return "nominal";
  if (reading.temperatureC > threshold.maxC) return "hot";
  if (reading.temperatureC < threshold.minC) return "cold";
  return "ok";
}
